import {
  useContext, useRef, useState, useEffect, createRef
} from 'react';

import { RecaptchaLibContext, RecaptchaLibDispatchContext } from '../context/context';
import { RecaptchaLibToken } from '../types/types';
import renderV2 from '../functions/renderV2';

export interface RecaptchaLibV2Props {
  callback: (token: RecaptchaLibToken) => void,
}

function RecaptchaLibV2({ callback }: RecaptchaLibV2Props) {
  const { loaded, checkboxFallbackSiteKey } = useContext(RecaptchaLibContext);
  const dispatchContext = useContext(RecaptchaLibDispatchContext);

  const elementRef = createRef<HTMLDivElement>();
  const [widgetId, setWidgetId] = useState<number>();
  const callbackRef = useRef<((token: RecaptchaLibToken) => void) | undefined>(undefined);
  callbackRef.current = callback;

  useEffect(() => {
    if (!loaded || !checkboxFallbackSiteKey || !elementRef) {
      return;
    }

    if (typeof widgetId !== 'undefined') {
      return;
    }

    // Check if grecaptcha is actually available
    if (!window.grecaptcha) {
      return;
    }

    const handleV2 = (newToken: RecaptchaLibToken) => {
      dispatchContext({ checkboxFallbackToken: newToken });
      if (callbackRef.current) {
        callbackRef.current(newToken);
      }
    };

    const handleV2Expired = () => {
      dispatchContext({ checkboxFallbackToken: null });
      if (callbackRef.current) {
        callbackRef.current(null);
      }
    };

    const handleV2Error = () => {
      dispatchContext({ checkboxFallbackToken: null });
      if (callbackRef.current) {
        callbackRef.current(null);
      }
    };

    try {
      const newWidgetId = renderV2(checkboxFallbackSiteKey, elementRef, handleV2, handleV2Expired, handleV2Error);
      setWidgetId(newWidgetId);
    } catch (error) {
      console.error('Error rendering recaptcha v2 widget', error);
    }
  }, [loaded, checkboxFallbackSiteKey, elementRef, widgetId, dispatchContext]);

  return (
    <div ref={elementRef} />
  );
}

export default RecaptchaLibV2;
